import { memo } from 'react';
import { OPENING_CATEGORY_LABELS } from '../../../potree/sketchObjects/custom/openings/builtinOpenings';
import { DIMENSION_CATEGORY_LABEL } from '../../../potree/sketchObjects/types';
import { T, useT, useTranslator } from '../../../translation/src';
import { useObjectDefinitionUpdateDetailsMutation } from '../../../types/graphqlTypes';
import { TranslatedLabel } from '../../objectLibrary/TranslatedLabel';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, PanelSection, SelectField, TextField } from '../panels/fields';

/** Name, category, tags and description: saved immediately, not part of the draft. */
const ObjectDetails_: React.FC = () => {
  const { definition, organisationId, categories } = useObjectEditor();
  const { spec } = useEditorState();
  const [updateDetails] = useObjectDefinitionUpdateDetailsMutation();
  const translate = useTranslator();
  // Built-in category labels are translation keys; the organisation's own are plain text.
  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.builtin ? translate(c.label, { swc: true }) : c.label }));
  const tagsPlaceholder = useT('e.g. wardrobe, storage', { swc: true });
  const opening = spec.opening ?? null;

  const saveDetails = (patch: Partial<{ name: string; description: string; categoryId: string; tags: string[] }>) =>
    updateDetails({
      variables: {
        organisationId,
        definitionId: definition.id,
        name: patch.name ?? definition.name,
        description: patch.description ?? definition.description ?? '',
        categoryId: patch.categoryId ?? definition.categoryId,
        tags: patch.tags ?? definition.tags,
      },
    });

  return (
    <PanelSection title={<T _str="object" />}>
      <FieldRow label={<T _str="name" swc />}>
        <TextField value={definition.name} maxLength={80} onCommit={(name) => name.trim() && void saveDetails({ name: name.trim() })} />
      </FieldRow>
      <FieldRow label={<T _str="category" swc />}>
        {opening || spec.dimension ? (
          <div className="py-1 text-sm text-gray-500 dark:text-gray-400">
            <TranslatedLabel label={opening ? OPENING_CATEGORY_LABELS[opening.kind] : DIMENSION_CATEGORY_LABEL} swc />
          </div>
        ) : (
          <SelectField value={definition.categoryId} options={categoryOptions} onChange={(categoryId) => void saveDetails({ categoryId })} />
        )}
      </FieldRow>
      <FieldRow label={<T _str="search tags" swc />}>
        <TextField
          value={definition.tags.join(', ')}
          placeholder={tagsPlaceholder}
          onCommit={(text) =>
            void saveDetails({
              tags: text
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
            })
          }
        />
      </FieldRow>
      <FieldRow label={<T _str="description" swc />}>
        <TextField value={definition.description ?? ''} maxLength={500} onCommit={(description) => void saveDetails({ description })} />
      </FieldRow>
    </PanelSection>
  );
};

export const ObjectDetails = memo(ObjectDetails_);
